import {useCallback, useEffect, useRef} from 'react';
import axios from 'axios';

/**
 * Pollt den Status einer Weclapp-Übertragung (Worker-Job) bis Abschluss.
 *
 * poll(jobId, { interval, onProgress }) liefert ein Promise:
 *  - resolve(data) bei status === 'completed'
 *  - reject(error) bei status === 'failed' (error.transmission enthält die Statusdaten)
 *    oder bei einem Netzwerk-/HTTP-Fehler.
 * onProgress(data) wird bei jedem Tick aufgerufen (auch für pending/processing).
 * Das Polling wird beim Unmount der Komponente automatisch abgebrochen.
 */
export default function useTransmissionPolling() {
    const timerRef = useRef(null);
    const cancelledRef = useRef(false);

    useEffect(() => {
        return () => {
            cancelledRef.current = true;
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    const poll = useCallback((jobId, {interval = 2000, onProgress} = {}) => {
        cancelledRef.current = false;

        return new Promise((resolve, reject) => {
            const tick = async () => {
                if (cancelledRef.current) {
                    return;
                }
                try {
                    const {data} = await axios.get(`/weclapp/transmission/status/${jobId}`);
                    if (onProgress) {
                        onProgress(data);
                    }

                    if (data.status === 'completed') {
                        resolve(data);
                        return;
                    }
                    if (data.status === 'failed') {
                        const err = new Error(data.error_detail || data.error_message || 'Übertragung fehlgeschlagen');
                        err.transmission = data;
                        reject(err);
                        return;
                    }

                    // pending / processing -> weiter pollen
                    timerRef.current = setTimeout(tick, interval);
                } catch (e) {
                    reject(e);
                }
            };

            tick();
        });
    }, []);

    return {poll};
}
